import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { validateImageFile } from '@/lib/firebase/storage';

export interface ImageUploadProps {
  /**
   * Current image URL if editing
   */
  currentImageUrl?: string;
  /**
   * Called when user selects a new image
   */
  onImageSelect: (file: File | null) => void;
  /**
   * Upload progress (0-100) when uploading
   */
  uploadProgress?: number;
  /**
   * Whether the upload is in progress
   */
  isUploading?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Label for the upload area
   */
  label?: string;
  /**
   * Help text shown below the upload area
   */
  helpText?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * ImageUpload - Reusable image upload component with drag & drop
 * 
 * Features:
 * - Drag and drop file upload
 * - Click to select files
 * - Image preview
 * - Upload progress indicator
 * - File validation (type and size)
 * - Error handling
 * - Current image display for editing
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onImageSelect,
  uploadProgress = 0,
  isUploading = false,
  error,
  label,
  helpText = 'Upload a square logo for your restaurant (JPEG, PNG, or WebP, max 5MB). Images will be automatically cropped to square.',
  required = false,
  className = '',
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    // Validate the file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      // Error will be handled by parent component
      onImageSelect(null);
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setSelectedFile(file);
    onImageSelect(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageSelect(null);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // Determine which image to show
  const displayImageUrl = previewUrl || currentImageUrl;
  const hasImage = !!displayImageUrl;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label - only render if label is provided */}
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg transition-all duration-200 ${
          dragActive
            ? 'border-primary bg-primary/10'
            : hasImage
            ? 'border-gray-200'
            : 'border-gray-300 hover:border-gray-400'
        } ${isUploading ? 'pointer-events-none' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleInputChange}
          className="hidden"
          disabled={isUploading}
        />

        <AnimatePresence mode="wait">
          {hasImage ? (
            // Image Preview
            <motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative group"
            >
              <div className="aspect-square w-full max-w-xs mx-auto p-3 sm:p-4">
                <img
                  src={displayImageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg"
                />
                
                {/* Overlay on hover */}
                <div className="absolute inset-3 sm:inset-4 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <p className="text-white text-xs sm:text-sm font-medium text-center px-2">Click to change image</p>
                </div>
              </div>

              {/* Remove button */}
              {selectedFile && !isUploading && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelection();
                  }}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </motion.button>
              )}
            </motion.div>
          ) : (
            // Upload Placeholder
            <motion.div
              key="placeholder"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col items-center justify-center py-6 sm:py-8 px-4"
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                dragActive ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'
              } transition-colors`}>
                {isUploading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : dragActive ? (
                  <Upload className="w-6 h-6" />
                ) : (
                  <ImageIcon className="w-6 h-6" />
                )}
              </div>
              
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {dragActive
                    ? 'Drop your image here'
                    : isUploading
                    ? 'Uploading...'
                    : 'Click to upload or drag and drop'
                  }
                </p>
                <p className="text-xs text-gray-500">
                  Square images work best (max 5MB)
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload Progress */}
        {isUploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute bottom-0 left-0 right-0 bg-white border-t rounded-b-lg p-3"
          >
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Uploading...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-primary h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-600"
        >
          {error}
        </motion.p>
      )}

      {/* Help Text */}
      {helpText && !error && (
        <p className="text-sm text-gray-500">
          {helpText}
        </p>
      )}

      {/* Current File Info */}
      {selectedFile && !isUploading && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between text-xs text-gray-500 bg-gray-50 rounded px-3 py-2"
        >
          <span>Selected: {selectedFile.name}</span>
          <span>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
        </motion.div>
      )}
    </div>
  );
};